import { Injectable } from '@angular/core';
import { Http, Response, URLSearchParams, RequestOptions } from '@angular/http';
import { Observable } from 'rxjs/Observable';

import { AuthHttpService } from 'app/security/authorization';
import { PaginationList } from 'app/shared';
import { environment } from 'environments/environment';
import { Credentials } from './credentials.model';
import { LoginReponse } from './login-response.model';
import { User } from './user.model';
import { UserMapper } from './user.mapper';
import { UserImport } from 'app/import/user-import.model';
import { UserImportMapper } from 'app/import/user-import.mapper';
import { UserGroupId } from 'app/group/user-group.model';

@Injectable()
export class UserRepository {

  constructor(
    private http: Http,
    private authHttp: AuthHttpService
  ) { }

  public autocomplete(name: string, role: string): Observable<User[]> {
    const params: URLSearchParams = new URLSearchParams();
    params.set('filters[role]', role);

    if (name) {
        params.set('filters[fullName]', name);
    }

    const requestOptions: RequestOptions = new RequestOptions();
    requestOptions.params = params;

    return this.authHttp.get(
        environment.apiUrl + 'user',
        requestOptions
      )
      .map(response => response.json())
      .map((response: any): User[] => response.items.map(UserMapper.map));
  }

  public login(email: string, password: string): Observable<LoginReponse> {
    return this.http.post(
        environment.apiUrl + 'login',
        new Credentials(email, password)
      )
      .map((response: Response): LoginReponse => {
          const responseJson: any = response.json();

          return new LoginReponse(
              responseJson.token,
              responseJson.userId
            );
      });
  }

  public resetPassword(email: string) {
    return this.http.post(
      environment.apiUrl + 'user/password/reset',
      { email }
    );
  }

  public setNewPassword(password: string, token: string) {
    return this.http.patch(
      environment.apiUrl + 'user/password/set/' + token,
      { password }
    );
  }

  public list(params: URLSearchParams = new URLSearchParams): Observable<PaginationList<User>> {
    return this.authHttp.get(
        environment.apiUrl + 'user',
        { search: params }
      )
      .map(response => response.json())
      .map((list: any): PaginationList<User> => new PaginationList<User>(
          list.items.map(UserMapper.map),
          list.total
        )
      );
  }

  public get(userId: string): Observable<User> {
    return this.authHttp.get(
        environment.apiUrl + 'user/' + userId
      )
      .map(response => response.json())
      .map(UserMapper.map);
  }

  public create(
    roles: string[],
    name: string,
    surname: string,
    phone: string,
    email: string,
    language: string,
    company: string,
    department: string,
    industry: string
  ) {
    return this.authHttp.post(
      environment.apiUrl + 'user',
      {
        roles,
        name,
        surname,
        phone,
        email,
        language,
        company,
        department,
        industry
      }
    )
    .map(response => response.json());
  }

  public createWithGroupAssign(
    roles: string[],
    name: string,
    surname: string,
    phone: string,
    email: string,
    language: string,
    company: string,
    department: string,
    industry: string,
    groupId: UserGroupId
  ) {
    return this.authHttp.post(
      environment.apiUrl + 'group/' + groupId + '/user',
      {
        roles,
        name,
        surname,
        phone,
        email,
        language,
        company,
        department,
        industry
      }
    );
  }

  public update(
    id: string,
    roles: string[],
    name: string,
    surname: string,
    phone: string,
    language: string,
    email: string,
    company: string,
    department: string,
    industry: string
  ) {
    return this.authHttp.put(
      environment.apiUrl + 'user/' + id,
      {
        name,
        roles,
        surname,
        phone,
        language,
        email,
        company,
        department,
        industry
      }
    );
  }

  public delete(userId: string) {
    return this.authHttp.delete(
      environment.apiUrl + 'user/' + userId,
      null
    );
  }

  public returnListOfImportedUsers(excelId: string): Observable<UserImport[]> {
    return this.authHttp.get(
        environment.apiUrl + 'user/import/' + excelId + '/validate'
      )
      .map(response => response.json())
      .map(userListImport => userListImport.map(UserImportMapper.map));
  }

  public importUsersToDatabase(excelId: string, groupId: UserGroupId) {
    if (groupId !== null) {

      return this.authHttp.post(
          environment.apiUrl + 'user/import/' + excelId + '/group/' + groupId, null
        );
    }

    return this.authHttp.post(
        environment.apiUrl + 'user/import/' + excelId, null
      );
  }
}



// WEBPACK FOOTER //
// /dist/src/app/user/user.repository.ts